// Contrail V3.7.0 — installable app support: service worker registration, update check, install prompt, layout choice.
// The service worker is registered only on the real sites (production build, not localhost), so it can never hide
// changes during development. For local testing set localStorage "fw.sw" = "1".

type BIPEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };
let deferredInstall: BIPEvent | null = null;
let waitingWorker: ServiceWorker | null = null;
const emit = (name: string) => window.dispatchEvent(new Event(name));

export function initPwa() {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredInstall = e as BIPEvent; emit("fw-installable"); });
  window.addEventListener("appinstalled", () => { deferredInstall = null; emit("fw-installable"); });
  let allow = false;
  try { allow = localStorage.getItem("fw.sw") === "1"; } catch { /* ignore */ }
  const real = import.meta.env.PROD && !/^(localhost|127\.|\[::1\])/.test(location.hostname);
  if (!("serviceWorker" in navigator) || !(real || allow)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").then((reg) => {
      const watch = (w: ServiceWorker | null) => {
        if (!w) return;
        w.addEventListener("statechange", () => {
          // a new version finished installing while an older one controls the page → offer a refresh
          if (w.state === "installed" && navigator.serviceWorker.controller) { waitingWorker = w; emit("fw-update"); }
        });
      };
      if (reg.waiting && navigator.serviceWorker.controller) { waitingWorker = reg.waiting; emit("fw-update"); }
      reg.addEventListener("updatefound", () => watch(reg.installing));
      // look for a new deploy when the app comes back to the foreground, and hourly
      const check = () => reg.update().catch(() => {});
      document.addEventListener("visibilitychange", () => { if (!document.hidden) check(); });
      setInterval(check, 3600_000);
    }).catch(() => { /* not fatal: the app still works online */ });
    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => { if (!reloaded) { reloaded = true; location.reload(); } });
  });
}

export const updateReady = () => !!waitingWorker;
export function applyUpdate() {
  if (waitingWorker) waitingWorker.postMessage({ type: "SKIP_WAITING" }); else location.reload();
}

export const canInstall = () => !!deferredInstall;
export async function promptInstall() {
  if (!deferredInstall) return false;
  const e = deferredInstall; deferredInstall = null;
  await e.prompt();
  const r = await e.userChoice.catch(() => ({ outcome: "dismissed" }));
  emit("fw-installable");
  return r.outcome === "accepted";
}

export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || (navigator as any).standalone === true;
export const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// ---- which layout to open after sign-in ("Open desktop / mobile version" is remembered on this device)
const LAYOUT_KEY = "fw.layout";
export function layoutOverride(): "mobile" | "desktop" | null {
  try { const v = localStorage.getItem(LAYOUT_KEY); return v === "mobile" || v === "desktop" ? v : null; } catch { return null; }
}
export function setLayoutOverride(v: "mobile" | "desktop") { try { localStorage.setItem(LAYOUT_KEY, v); } catch { /* ignore */ } }
export function preferMobile() {
  const o = layoutOverride();
  if (o) return o === "mobile";
  return isStandalone() || window.innerWidth < 760;
}
export const homePath = () => (preferMobile() ? "/m" : "/map");
